import { useEffect, useState } from "react";
import { useAppDispatch } from "../../hooks/StoreHooks";
import { ServiceModule } from "../../types/serviceModuleTypes";
import { IconButton, Stack, Typography } from "@mui/material";
import DefaultIcon from "../../components/common/DefaultIcon";
import { deleteServiceModuleThunk } from "../../store/thunk/serviceModulesThunk";
import { StyledTile } from "./ServiceModules.styles";
import { Edit } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useNewestServiceModule } from "../../store/selectors/serviceModulesSelector";
import ServiceModuleEditor from "./ServiceModuleEditor";
import { defaultIcon } from "./ServiceModulesContainer";
import { theme } from "../../theme";
import { PermissionEntityType } from "../../types/permissionType";
import { usePermissionChecker } from "../../hooks/usePermissionChecker";

interface Props {
    module: ServiceModule;
    setIsReordering: (value: boolean) => void;
    onCardClick: (module: ServiceModule) => void;
}

export default function ServiceModuleCard(props: Props) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [isModuleEditing, setIsModuleEditing] = useState(false);
    const newestServiceModule = useNewestServiceModule()

    const { userHasPermission } = usePermissionChecker();
    const canEditServiceModule = userHasPermission(PermissionEntityType.ServiceModules, "Edit") ||
        userHasPermission(PermissionEntityType.ServiceModules, "Edit", props.module.id);

    useEffect(() => {
        if (newestServiceModule?.id === props.module.id) {
            setIsModuleEditing(true);
            props.setIsReordering(true);
        }
    }, [newestServiceModule?.id, props.module.id]);

    const handleDeleteModule = (moduleId: number) => {
        dispatch(deleteServiceModuleThunk(moduleId));
        setIsModuleEditing(false);
        props.setIsReordering(false);
    };

    return (
        <StyledTile onClick={() => props.onCardClick(props.module)}>
            {canEditServiceModule && (
                <IconButton
                    size="small"
                    sx={{
                        position: "absolute",
                        top: 5,
                        right: 5,
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsModuleEditing(true);
                        props.setIsReordering(true);
                    }}>
                    <Edit />
                </IconButton>
            )}

            <Stack gap={1.5} alignItems="center">
                <DefaultIcon
                    iconName={props.module.iconName ?? defaultIcon(props.module.type)}
                    colorHex={props.module.colorHex ?? theme.palette.primary.main}
                    iconUrl={props.module.iconUrl}
                />
                <Typography variant="subtitle1">{props.module.name}</Typography>
                <Stack gap={0.5} alignItems="flex-start">
                    <Typography variant="caption" color="textSecondary">
                        {t("serviceModulesPage.type")}: {props.module.type}
                    </Typography>
                    <Typography
                        variant="caption"
                        sx={{
                            color: props.module.isVisible ? "green" : "textSecondary",
                            fontWeight: props.module.isVisible ? "bold" : "normal"
                        }}
                    >
                        {props.module.isVisible ? t("serviceModulesPage.isVisible") : t("serviceModulesPage.isNotVisible")}
                    </Typography>
                </Stack>
            </Stack>

            {isModuleEditing && (
                <ServiceModuleEditor
                    module={props.module}
                    onClose={() => {
                        setIsModuleEditing(false);
                        props.setIsReordering(false);
                    }}
                    onDelete={handleDeleteModule}
                />
            )}
        </StyledTile>);
}